import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["title", "headingText", "Content", "defaultOpen", "referenceTabIndex", "open", "onOpenChange", "onClose", "onClick", "onMouseDown", "use", "placement", "autoPlacement", "delay", "animateOnToggle", "disabled", "role", "aria-live", "aria-label", "maxInlineSize", "zIndex", "tabIndex", "innerCssStyles", "_bodyElement", "children"];
import { Children, cloneElement, useRef, useEffect } from 'react';
import { isFragment } from 'react-is';
import devLogger from 'react-utils/devLogger';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import { syntheticEvent } from '../../utils/SyntheticEvent/SyntheticEvent';
import { useControllable } from '../../utils/useControllable/useControllable';
import { FLOATING_UI_PLACEMENTS_MAP } from './placementConstants';
import { useFloating, offset, flip, shift, arrow, hide, autoUpdate, useHover, useFocus, useDismiss, useInteractions, safePolygon, FloatingArrow, FloatingPortal } from '@floating-ui/react';
import styled, { useTheme } from 'styled-components';
import { TooltipHeader } from './TooltipHeader';
import { ChildrenWrapper } from '../ChildrenWrapper/ChildrenWrapper';
import { findDOMNode } from 'react-dom';
import { filterProps } from '../../../utils/filterProps';
import { useMapStylePropsToCSS } from '../../../hooks/useMapStylePropsToCSS';
import { AnimatePresence, m } from 'framer-motion';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const DEFAULT_DELAY = 300; // Exported for testing

const StyledFloatingContainer = styled.div.withConfig({
  displayName: "Tooltip__StyledFloatingContainer",
  componentId: "sc-1oy6m2a-0"
})(({
  $floatingStyles,
  $zIndex
}) => {
  return Object.assign({}, $floatingStyles, {
    zIndex: $zIndex // The z-index needs to be in the root-level tooltop element
  });
});
const StyledTooltipContainer = styled.div.withConfig({
  displayName: "Tooltip__StyledTooltipContainer",
  componentId: "sc-1oy6m2a-1"
})(({
  $use,
  $cssStyles,
  theme: _theme
}) => {
  const theme = _theme;
  const isLongform = $use === 'longform';
  return Object.assign({
    position: 'relative',
    display: 'block',
    boxShadow: theme.boxShadow['400'],
    lineHeight: '1.5',
    paddingInline: theme.space['400'],
    paddingBlock: '10px',
    textDecoration: 'none',
    wordWrap: 'break-word',
    fontSize: isLongform ? '12px' : '13px',
    borderRadius: theme.borderRadius['100'],
    backgroundColor: isLongform ? theme.color['fill-surface-default'] : $use === 'danger' ? theme.color['fill-accent-red-default'] : theme.color['fill-accent-neutral-default'],
    borderWidth: isLongform ? theme.borderWidth['100'] : undefined,
    borderStyle: isLongform ? 'solid' : undefined,
    borderColor: isLongform ? theme.color['border-core-default'] : undefined,
    color: isLongform ? undefined : theme.color['text-primary-on-fill-default']
  }, !isLongform ? {
    '& a': {
      color: theme.color['text-primary-on-fill-default'],
      textDecoration: 'underline',
      fontWeight: '600',
      '&:hover': {
        color: theme.color['text-interactive-default-pressed']
      },
      '&:active': {
        color: theme.color['fill-tertiary-default']
      }
    },
    '& svg[data-icon-name]': {
      color: theme.color['text-primary-on-fill-default']
    }
  } : undefined, $cssStyles);
});
const StyledTooltipText = styled.div.withConfig({
  displayName: "Tooltip__StyledTooltipText",
  componentId: "sc-1oy6m2a-2"
})(({
  $use,
  $cssStyles,
  theme: _theme
}) => {
  const theme = _theme;
  return Object.assign({
    '> :last-child': {
      marginBlockEnd: $use === 'longform' ? theme.space['0'] : undefined
    }
  }, $cssStyles);
});
export const Tooltip = _ref => {
  var _middlewareData$hide2;
  let {
      title,
      headingText,
      Content,
      defaultOpen = false,
      referenceTabIndex = 0,
      open,
      onOpenChange,
      onClose,
      onClick,
      onMouseDown,
      use = 'default',
      placement = 'top',
      autoPlacement = true,
      delay = DEFAULT_DELAY,
      animateOnToggle = true,
      disabled = false,
      role = 'tooltip',
      'aria-live': ariaLive,
      'aria-label': ariaLabel,
      maxInlineSize = use === 'longform' ? '280px' : '232px',
      zIndex = 1111,
      tabIndex,
      innerCssStyles: innerCssStylesProp = {},
      _bodyElement,
      children
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [controllableOpen, handleOpenChange] = useControllable(open, defaultOpen, onOpenChange);
  const computedDisabled = disabled || Content == null && !title;
  const computedOpen = controllableOpen && !computedDisabled;
  const {
    domProps,
    styleProps
  } = filterProps(Object.assign({}, rest, {
    maxInlineSize
  }));
  const outerCssStyles = useMapStylePropsToCSS(styleProps);
  const innerCssStyles = useMapStylePropsToCSS(innerCssStylesProp);
  const theme = useTheme();
  const arrowRef = useRef(null);
  const {
    refs,
    floatingStyles,
    context,
    placement: currentPlacement,
    middlewareData
  } = useFloating({
    placement: FLOATING_UI_PLACEMENTS_MAP[placement],
    open: computedOpen,
    onOpenChange: newIsOpen => {
      if (!computedDisabled) {
        handleOpenChange(syntheticEvent(newIsOpen));
        if (!newIsOpen && onClose) {
          onClose();
        }
      }
    },
    middleware: [offset(parseInt(theme.space['400'], 10)), autoPlacement ? flip() : undefined, autoPlacement ? shift() : undefined, arrow({
      element: arrowRef,
      padding: parseInt(theme.space['100'], 10)
    }), hide()],
    whileElementsMounted: autoUpdate
  });
  const hover = useHover(context, {
    delay: {
      open: delay,
      close: 0
    },
    handleClose: safePolygon()
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const {
    getReferenceProps,
    getFloatingProps
  } = useInteractions([hover, focus, dismiss]);
  const tooltipId = useUniqueId('tooltip-');
  const child = Children.only(children);
  const currentPlacementSide = currentPlacement.split('-')[0];
  const oppositeCurrentPlacementSide = currentPlacementSide === 'top' ? 'bottom' : currentPlacementSide === 'right' ? 'left' : currentPlacementSide === 'bottom' ? 'top' : 'right';
  if (process.env.NODE_ENV !== 'production') {
    if (isFragment(child)) {
      devLogger.warn({
        message: `Tooltip: child element cannot be a React.Fragment, and must support passing event handlers through to a DOM element.`,
        key: 'Tooltip: fragment not supported'
      });
    }
  }

  // Closes the tooltip when the reference element is hidden
  // jsdom doesn't have a layout engine, so we can't support this feature in tests
  useEffect(() => {
    var _middlewareData$hide;
    if (computedOpen && (_middlewareData$hide = middlewareData.hide) !== null && _middlewareData$hide !== void 0 && _middlewareData$hide.referenceHidden) {
      if (process.env.NODE_ENV === 'production') {
        handleOpenChange(syntheticEvent(false));
      }
    }
  }, [computedOpen, (_middlewareData$hide2 = middlewareData.hide) === null || _middlewareData$hide2 === void 0 ? void 0 : _middlewareData$hide2.referenceHidden, handleOpenChange]);
  let clonedChild;
  if (!computedDisabled) {
    clonedChild = /*#__PURE__*/cloneElement(child, getReferenceProps(Object.assign({}, child.props, {
      tabIndex: referenceTabIndex,
      // UIComponents#4949
      'aria-describedby': computedOpen ? `${child.props['aria-describedby'] || ''} ${tooltipId}`.trim() : child.props['aria-describedby']
    })));
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ChildrenWrapper
    // @ts-expect-error - Parameter 'childNode' implicitly has an 'any' type.
    , {
      ref: childNode => {
        /* eslint-disable-next-line react/no-find-dom-node */
        refs.setReference(findDOMNode(childNode));
      },
      children: clonedChild || child
    }), /*#__PURE__*/_jsx(AnimatePresence, {
      children: computedOpen && /*#__PURE__*/_jsx(FloatingPortal, {
        // Needed for AnimatePresence (see https://www.framer.com/motion/animate-presence/##exit-animations)
        root: _bodyElement || document.body,
        children: /*#__PURE__*/_jsx(StyledFloatingContainer, Object.assign({}, getFloatingProps(Object.assign({}, domProps, {
          role,
          tabIndex,
          onClick,
          onMouseDown,
          id: tooltipId,
          'aria-live': ariaLive,
          'aria-label': ariaLabel
        })), {
          ref: refs.setFloating,
          $floatingStyles: floatingStyles,
          $zIndex: zIndex,
          children: /*#__PURE__*/_jsx(m.div, {
            initial: {
              opacity: 0,
              transform: 'scale(0.75)',
              transformOrigin: oppositeCurrentPlacementSide
            },
            animate: {
              opacity: 1,
              transform: 'scale(1)',
              transformOrigin: oppositeCurrentPlacementSide
            },
            exit: {
              opacity: 0,
              transform: 'scale(0.75)',
              transformOrigin: oppositeCurrentPlacementSide
            },
            transition: {
              duration: animateOnToggle ? 0.2 : 0
            },
            children: /*#__PURE__*/_jsxs(StyledTooltipContainer, {
              $use: use,
              $cssStyles: outerCssStyles,
              children: [Content == null ? /*#__PURE__*/_jsxs(StyledTooltipText, {
                $use: use,
                $cssStyles: innerCssStyles,
                children: [headingText && /*#__PURE__*/_jsx(TooltipHeader, {
                  use: use,
                  children: headingText
                }), title]
              }) : /*#__PURE__*/_jsx(Content, {}), /*#__PURE__*/_jsx(FloatingArrow, {
                ref: arrowRef,
                context: context,
                fill: use === 'longform' ? theme.color['fill-surface-default'] : use === 'danger' ? theme.color['fill-accent-red-default'] : theme.color['fill-accent-neutral-default'],
                width: parseInt(theme.space['500'], 10),
                height: 10,
                stroke: use === 'longform' ? theme.color['border-core-default'] : undefined,
                strokeWidth: use === 'longform' ? parseInt(theme.borderWidth['100'], 10) : undefined,
                tipRadius: use === 'longform' ? undefined : 1
              })]
            })
          })
        }))
      }, "tooltip-floating-portal")
    })]
  });
};